import { useMemo, FormEvent, useCallback, Fragment } from 'react';
import { useTranslation } from 'react-i18next';
import { useSetup } from 'hooks/useSetup/useSetup';
import { values } from 'lodash';
import { TextField } from 'ui/shared/controls';
import { SetupDevice } from '../setupDevice/SetupDevice';
import { expandMacAddress } from 'util/cleanMacAddress';
import { ErrorMessage } from 'ui/shared/messages';
import { LoadingSpinner } from 'ui/shared/LoadingSpinner';
import { renameDevices } from 'logic/renameDevices';
import { Device, DeviceId } from 'shared/models';

require('./SetupDeviceGroup.css');

export type Props = {
    enabled: boolean;
    externalId: string;
    changeExternalId(value: string): void;
};

export const SetupDeviceGroup = ({ enabled, externalId, changeExternalId }: Props) => {
    const { t } = useTranslation();
    const { regCode, devices, group, changeGroupName, changeDeviceName, reload, busy } = useSetup();

    const groupId = group && group.id;

    const groupDevices = useMemo(() => {
        return values(devices)
            .filter(d =>
                groupId
                    ? d.groupId === groupId // If there is a group then show everything in the group
                    : d.properties.regCode === regCode // Otherwise, only show the display device by itself
            )
            .sort((a, b) => {
                const aName = a.displayName || `_${expandMacAddress(a.macAddress)}` || `__${a.id}`;
                const bName = b.displayName || `_${expandMacAddress(b.macAddress)}` || `__${b.id}`;
                return aName.localeCompare(bName);
            });
    }, [ devices, groupId, regCode ]);

    const name = group ? group.displayName : '';

    const handleChangeName = useCallback((e: FormEvent<HTMLInputElement>) => {
        if (!groupId) return;
        
        const displayName = e.currentTarget.value;
        changeGroupName(groupId, displayName);

        const deviceNamesById: Record<DeviceId, Device> = {};
        for (const device of groupDevices) {
            deviceNamesById[device.id] = device;
        }
        
        const newDeviceNamesById = renameDevices(displayName, deviceNamesById, t);
        for (const deviceId of Object.keys(newDeviceNamesById)) {
            const deviceName = newDeviceNamesById[deviceId];
            changeDeviceName(deviceId, deviceName);
        }
    }, [ t, groupId, groupDevices, changeGroupName, changeDeviceName ]);

    const hasCamera = groupDevices.find(d => d.deviceClass === 'camera');

    // Note: I'd rather pass the value and onChange directly to an input element
    // but the TextField control, which has all the right layout and style, was
    // developed for use with ReduxForm. Eventually I'd like to eliminate
    // ReduxForm... and when that happens, this code should be simiplified.

    return (
        <div
            id={ `setup-device-group-${groupId}` }
            className="ui form setup-device-group"
        >
            <TextField
                id={ `setup-device-group-${groupId}-name` }
                labelKey="SETUP.ELEVATOR_NAME"
                value={ name }
                onChange={ handleChangeName }
                disabled={ busy || !enabled }
                required={ true }
                autoFocus={ true }
                maxLength={ 32 }
            />

            <div className="ui grid">
                <div className="ui sixteen wide column">
                    <TextField
                        id={ `setup-device-group-${groupId}-extid` }
                        label={ t(`SETUP.EXTERNAL_ID`) }
                        value={ externalId }
                        autoComplete="off"
                        onChange={ e => changeExternalId(e.currentTarget.value) }
                        disabled={ busy || !enabled }
                        maxLength={ 40 }
                    />
                </div>
            </div>

            <h2>
                { t('SETUP.DEVICES') }
            </h2>
            { groupDevices.map(d =>
                <SetupDevice
                    key={ d.id }
                    deviceId={ d.id }
                />
            ) }
            { !hasCamera &&
                <Fragment>
                    <ErrorMessage message={ t('SETUP.ERROR.NO_CAMERA') }/>
                    <div>
                        <button
                            id="setup-device-group-reload"
                            className="ui default red button"
                            type="button"
                            disabled={ busy }
                            onClick={ reload }
                        >
                            { t('COMMAND.RELOAD') }
                        </button>
                        { busy &&
                            <LoadingSpinner size="small"/>
                        }
                    </div>
                </Fragment>
            }
        </div>
    );
};
